import {
    Component,
    effect,
    OnInit,
    ChangeDetectionStrategy
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { UsersService } from '../users-service/users.service';
import { AuthService } from '../../../authentication/service/auth.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgIf } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmUserDisableComponent } from './confirm-user-disable/confirm-user-disable.component';
import { WelcomeLoaderComponent } from '../../../common/welcome-loader/welcome-loader.component';
import {
    CustomerDiviceInfoComponent,
    CustomerDeviceRow
} from './customer-divice-info/customer-divice-info.component';
import { GrpcStatusEvent } from '@ngx-grpc/common';
import {
    GetUserResponse,
    UserGroupMessage
} from '../../../protos/generated/admin.pb';
import { PlantsUserComponent } from './plants-user/plants-user.component';
import { PlantsService } from '../../plants/services/plants.service';
import { PlantDto } from '../../plants/Models/plantDto.interface';
import { TimestampDatePipe } from '../../plants/shared/timestamp-date.pipe';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-user-details',
    standalone: true,
    imports: [
        MatCardModule,
        MatButtonModule,
        MatMenuModule,
        MatTooltipModule,
        NgIf,
        RouterLink,
        WelcomeLoaderComponent,
        CustomerDiviceInfoComponent,
        PlantsUserComponent,
        TimestampDatePipe,
        MatIconModule,
        TranslatePipe
    ],
    templateUrl: './user-details.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './user-details.component.scss'
})
export class UserDetailsComponent implements OnInit {
    userInfo: GetUserResponse = new GetUserResponse();
    userGroups: UserGroupMessage[] = [];
    roles: any[] = [];
    userId!: number;
    isLoading = false;
    isCustomer = false;
    ownerName?: string = '';
    plantList: PlantDto[] = [];
    totalCount = 0;

    customerDevices: CustomerDeviceRow[] = [];
    customerDevicesLoading = false;

    constructor(
        private usersService: UsersService,
        private route: ActivatedRoute,
        private authService: AuthService,
        private dialog: MatDialog,
        private router: Router,
        private customerassetsService: PlantsService
    ) {
        effect(() => {
            if (this.usersService.getUserDisabledStatusSignal()()) {
                this.getUserById(this.userId);
                this.usersService.notifyUserDisabledStatusChange();
            }
        });
    }

    ngOnInit(): void {
        const idParam =
            this.route.snapshot.paramMap.get('id') ??
            this.route.parent?.snapshot.paramMap.get('id') ??
            this.route.snapshot.queryParamMap.get('id') ??
            this.route.parent?.snapshot.queryParamMap.get('id');

        this.userId = idParam ? +idParam : NaN;
        this.getUserById(this.userId);
    }

    getUserById(userId: number) {
        this.isLoading = true;

        this.usersService.getUser(userId).subscribe({
            next: response => {
                this.userInfo = response;
                this.userGroups = response.userGroups;
                this.isCustomer = this.userGroups.some(
                    group => group.title === 'Customer'
                );
                this.ownerName = response.username;

                this.getPlantList(userId);
                this.isLoading = false;
            },
            error: (error: GrpcStatusEvent) => {
                this.isLoading = false;

                if (error.statusCode === 16) {
                    this.authService.logOut();
                } else if (
                    error.statusCode === 5 &&
                    error.statusMessage === 'User not found!'
                ) {
                    this.router.navigate(['/not-found']);
                } else {
                    console.error(error);
                }
            }
        });
    }

    openConfirmDialog() {
        const buttonElement = document.activeElement as HTMLElement;
        buttonElement.blur();

        this.dialog.open(ConfirmUserDisableComponent, {
            data: this.userInfo
        });
    }

    private extractPlantArray(res: any): any[] {
        if (Array.isArray(res?.plantsFullDetailResponse)) {
            return res.plantsFullDetailResponse;
        }

        if (Array.isArray(res?.plantsFullDetailResponses)) {
            return res.plantsFullDetailResponses;
        }

        if (Array.isArray(res)) {
            return res;
        }

        if (res?._plantInfo || res?.plantInfo) {
            return [res];
        }

        return [];
    }

    getPlantList(userId: number) {
        this.customerDevicesLoading = true;

        this.customerassetsService
            .getPlantFullDetailByUserId(userId)
            .subscribe({
                next: (res: any) => {
                    const rawArray = this.extractPlantArray(res);

                    this.plantList = rawArray.map((item: any) => {
                        const info = item?.plantInfo ?? item?._plantInfo ?? {};

                        const toDate = (val: any) => {
                            if (!val) return undefined;

                            if (typeof val === 'string') {
                                const d = new Date(val);
                                return isNaN(d.getTime()) ? undefined : d;
                            }

                            if (val?._seconds != null) {
                                return new Date(Number(val._seconds) * 1000);
                            }

                            if (val?.seconds != null) {
                                return new Date(Number(val.seconds) * 1000);
                            }

                            return undefined;
                        };

                        const createdAt = toDate(
                            info._CreatedAt ??
                                info.CreatedAt ??
                                info._createdAt ??
                                info.createdAt
                        );
                        const lastUpdate = toDate(
                            info._lastUpdate ??
                                info._LastUpdate ??
                                info.lastUpdate
                        );

                        return {
                            id: info._id ?? info.id,
                            plantOwner: info._plantOwner ?? info.plantOwner,
                            cityId: info._cityId ?? info.cityId,
                            userName: info._userName ?? info.userName,
                            email: info._email ?? info.email,
                            phoneNumber: info._phoneNumber ?? info.phoneNumber,
                            latitude: info._latitude ?? info.latitude,
                            longitude: info._longitude ?? info.longitude,
                            plantImage:
                                info._plantImage ?? info.plantImage ?? '',
                            address:
                                info._Address ??
                                info.Address ??
                                info._address ??
                                info.address ??
                                '',
                            disabled: info._disabled ?? info.disabled ?? false,
                            createdAt,
                            lastUpdate,
                            userId: info._userId ?? info.userId,
                            lowPriceThreshold:
                                info._lowPriceThreshold ??
                                info.lowPriceThreshold,
                            priceClass: info._priceClass ?? info.priceClass,
                            customerType:
                                info._customerType ?? info.customerType
                        } as any;
                    });

                    this.customerDevices = rawArray.flatMap((item: any) => {
                        const info = item?.plantInfo ?? item?._plantInfo ?? {};
                        const inverters =
                            item?.inverters ?? item?._inverters ?? [];

                        const owner =
                            info?.plantOwner ?? info?._plantOwner ?? '-';
                        const lowPriceThreshold =
                            info?.lowPriceThreshold ??
                            info?._lowPriceThreshold ??
                            '-';

                        const timezone =
                            info?.zoneId ??
                            info?._zoneId ??
                            info?.timeZone ??
                            info?._timeZone ??
                            '-';

                        return inverters.map((inv: any) => ({
                            settingId: inv?.id ?? inv?._id ?? '-',
                            serialNumber:
                                inv?.serialNumber ?? inv?._serialNumber ?? '-',
                            owner,
                            systemCapacity:
                                inv?.maxProductionCapacity ??
                                inv?._maxProductionCapacity ??
                                '-',
                            smartCharge:
                                inv?.batteryChargePowerRateKw ??
                                inv?._batteryChargePowerRateKw ??
                                '-',
                            Timezone: timezone,
                            panelPower:
                                inv?.totalPanelPowerKwh ??
                                inv?._totalPanelPowerKwh ??
                                '-',
                            lowPriceThreshold
                        }));
                    });

                    this.totalCount = this.plantList.length;
                    this.customerDevicesLoading = false;
                },
                error: err => {
                    console.error('getPlantList error:', err);
                    this.plantList = [];
                    this.customerDevices = [];
                    this.totalCount = 0;
                    this.customerDevicesLoading = false;
                }
            });
    }
}
